import { CurrencyInr, Medal, Rocket, Star, Trophy, UserCircle } from '@phosphor-icons/react';
import GoogleReviewsSection from '../components/GoogleReviewsSection';
import { GatedVideo, ViewerProvider } from '../components/GatedVideo';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import rajeshImg from '../assets/images/rajesh p.jpeg';
import karthikImg from '../assets/images/karthik.jpeg';
import arjunImg from '../assets/images/arjun.jpeg';
import harishImg from '../assets/images/Harish K.jpeg';
import naveenImg from '../assets/images/Naveen Raj.jpeg';
import vikramImg from '../assets/images/Vikram N.jpeg';
import meghanaImg from '../assets/images/Meghana K.jpeg';
import akashImg from '../assets/images/Akash V.jpeg';
import sathishImg from '../assets/images/Sathish Kumar.jpeg';
import rohitImg from '../assets/images/Rohit Prakash.jpeg';
import ajayImg from '../assets/images/Ajay Krishna.jpeg';

interface Student {
  name: string;
  role: string;
  course: string;
  image: string | null;
  story: string;
  highlight: string;
  tag: string;
  color: string;
  gradient: string;
}

const students: Student[] = [
  {
    name: 'Rajesh P.', role: 'Backend Developer', course: 'Full Stack Python',
    image: rajeshImg, color: '#10b981', gradient: 'linear-gradient(135deg,#10b981,#059669)',
    highlight: 'Placed in 42 Days', tag: 'Python',
    story: 'My mentor worked with me every day — debugging my code, reviewing projects, and prepping me for real interviews. The 50% Pay After Placement model meant I could focus 100% on learning without any financial stress. Vista Tech\'s personal attention made all the difference.',
  },
  {
    name: 'Priyadharshini M.', role: 'Data Analyst', course: 'Data Analytics',
    image: null, color: '#ec4899', gradient: 'linear-gradient(135deg,#ec4899,#db2777)',
    highlight: 'Real Dashboards, Real Placement', tag: 'Data Analytics',
    story: 'Structured training with real-time dashboards gave me clarity on how companies use data. My mentor helped me connect technical concepts to business problems. The HR induction made the transition from learning to interview smooth and Vista Tech\'s PAP model made it completely risk-free.',
  },
  {
    name: 'Harish K.', role: 'AI/ML Engineer', course: 'AI/ML + 50% PAP',
    image: harishImg, color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    highlight: 'Landed the Right Role in AI', tag: 'AI/ML',
    story: 'I always wanted to work in AI but didn\'t have a clear roadmap. My Vista Tech mentor designed a personalised learning path for me — from Python basics to ML model deployment. The guidance during mock interviews completely changed my confidence. Zero financial risk with the PAP model.',
  },
  {
    name: 'Shanmugapriya R.', role: 'Java Full Stack Dev', course: 'Full Stack Java',
    image: null, color: '#6366f1', gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)',
    highlight: 'Cracked Interview in First Attempt', tag: 'Java',
    story: 'The training was intense but incredibly practical. My mentor didn\'t just teach Spring Boot — we deployed real microservices together. Personal sessions helped me understand production-level architecture. Vista Tech\'s 50% PAP model removed every financial worry so I could focus entirely on my growth.',
  },
  {
    name: 'Naveen Raj', role: 'ML Engineer', course: 'AI/ML + 50% PAP',
    image: naveenImg, color: '#14b8a6', gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)',
    highlight: 'Python Basics to ML Roles', tag: 'AI/ML',
    story: 'The AI track gave me a clear roadmap from Python to advanced ML model implementation. My mentor\'s sessions during final rounds boosted my confidence tremendously. Knowing I\'d only pay the remaining 50% after getting placed made me fully commit to the program.',
  },
  {
    name: 'Vikram N.', role: 'Java Backend Developer', course: 'Full Stack Java',
    image: vikramImg, color: '#0ea5e9', gradient: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
    highlight: 'Structured Path to Selection', tag: 'Java',
    story: 'Spring Boot and APIs were taught in a project-first, structured way. My Vista Tech mentor personalised each session based on my pace and specific gaps. The mock interview training, guided personally by my mentor, played a major role in my selection. The 50% PAP showed Vista Tech\'s confidence in what they teach.',
  },
  {
    name: 'Divya R.', role: 'Full Stack Developer', course: 'Full Stack Python',
    image: null, color: '#a855f7', gradient: 'linear-gradient(135deg,#a855f7,#9333ea)',
    highlight: 'End-to-End App Builder', tag: 'Python',
    story: 'Building end-to-end applications completely changed how I approach coding. My mentor at Vista Tech guided every project, from database design to UI deployment. Interview preparation and role mapping gave me confidence during final rounds. The pay-after-placement model meant Vista Tech was as invested as I was.',
  },
  {
    name: 'Meghana K.', role: 'Java Developer', course: 'Full Stack Java',
    image: meghanaImg, color: '#f97316', gradient: 'linear-gradient(135deg,#f97316,#ea580c)',
    highlight: 'Guided, Not Random Placement', tag: 'Java',
    story: 'I started with basic programming and felt overwhelmed. My Vista Tech mentor\'s structured guidance and hands-on project exposure helped me grow both technically and professionally. The placement process felt guided, not random. The 50% PAP model made taking this step feel completely safe.',
  },
  {
    name: 'Akash V.', role: 'Python Developer', course: 'Full Stack Python',
    image: akashImg, color: '#22c55e', gradient: 'linear-gradient(135deg,#22c55e,#16a34a)',
    highlight: 'Interview-Ready, Not Just Trained', tag: 'Python',
    story: 'The best part was how Vista Tech screened me before sending me to interviews. I wasn\'t pushed unprepared into rounds. My mentor personally polished my resume, refined my projects, and ran mock technical interviews. The 50% PAP model proved Vista Tech only profits when I do.',
  },
  {
    name: 'Sathish Kumar', role: 'Full Stack Developer', course: 'Full Stack Java',
    image: sathishImg, color: '#3b82f6', gradient: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
    highlight: 'Real Production Flow Experience', tag: 'Java',
    story: 'The project-based training helped me understand real production workflows, not just textbook concepts. My mentor at Vista Tech ensured I attended only interviews that matched my skillset. The HR induction support was personal and thorough. Vista Tech truly stands behind their promises.',
  },
  {
    name: 'Rohit Prakash', role: 'Data Analyst', course: 'Data Analytics',
    image: rohitImg, color: '#8b5cf6', gradient: 'linear-gradient(135deg,#8b5cf6,#6d28d9)',
    highlight: 'Case-Study Approach Worked', tag: 'Data Analytics',
    story: 'The case-study approach made every analytics concept practical and job-relevant. My mentor guided me through real datasets and helped me build dashboards employers immediately recognised. Vista Tech\'s 50% PAP model gave me the clarity and confidence to position myself correctly during interviews.',
  },
];

// ── Blank Avatar ────────────────────────────────────────────────────────────
const BlankAvatar = ({ name, gradient }: { name: string; gradient: string }) => (
  <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', fontWeight: '900', color: 'white' }}>
    {name.split(' ').map(w => w[0]).slice(0, 2).join('')}
  </div>
);

// ── Story Card ──────────────────────────────────────────────────────────────
const StoryCard = ({ s, i }: { s: Student; i: number }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: (i % 2) * 0.12 }}
      style={{
        background: 'white',
        borderRadius: '28px',
        overflow: 'hidden',
        border: '1px solid rgba(43,34,64,0.07)',
        boxShadow: '0 8px 32px rgba(43,34,64,0.07)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s, box-shadow 0.3s',
        cursor: 'default',
      }}
      whileHover={{ y: -8, boxShadow: `0 24px 60px ${s.color}22` }}
    >
      {/* Gradient top bar */}
      <div style={{ height: '5px', background: s.gradient }} />

      <div style={{ padding: '32px', display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Photo column */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: `0 0 0 3px ${s.color}30, 0 0 0 6px ${s.color}12`,
            border: `2px solid ${s.color}60`,
          }}>
            {s.image ? (
              <img src={s.image} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <BlankAvatar name={s.name} gradient={s.gradient} />
            )}
          </div>
          {/* Vertical line connector */}
          <div style={{ width: '2px', height: '40px', background: `linear-gradient(${s.color}40, transparent)`, borderRadius: '2px' }} />
          {/* Star ratings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
            {[0,1,2,3,4].map(j => (
              <Star key={j} size={10} weight="fill" color="#F0B429" />
            ))}
          </div>
        </div>

        {/* Content column */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name + badge */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
            <div>
              <div style={{ fontWeight: '800', color: '#2b2240', fontSize: '1.05rem', lineHeight: 1.2 }}>{s.name}</div>
              <div style={{ fontWeight: '600', color: s.color, fontSize: '0.82rem', marginTop: '3px' }}>{s.role}</div>
            </div>
            <span style={{
              flexShrink: 0, padding: '3px 10px', borderRadius: '6px',
              background: `${s.color}12`, color: s.color,
              fontSize: '0.68rem', fontWeight: '700',
              border: `1px solid ${s.color}25`,
            }}>{s.tag}</span>
          </div>

          {/* Course */}
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '14px', background: '#f8f7ff', display: 'inline-block', padding: '3px 10px', borderRadius: '4px', border: '1px solid rgba(43,34,64,0.06)' }}>
            {s.course}
          </div>

          {/* Quote */}
          <div style={{ position: 'relative', marginBottom: '18px' }}>
            <span style={{ position: 'absolute', top: '-4px', left: '-2px', fontSize: '3rem', lineHeight: 1, color: `${s.color}15`, fontFamily: 'Georgia,serif', fontWeight: '900', pointerEvents: 'none' }}>"</span>
            <p style={{ color: '#4b5563', fontSize: '0.862rem', lineHeight: 1.78, paddingTop: '4px', position: 'relative', zIndex: 1, margin: 0 }}>
              {s.story}
            </p>
          </div>

          {/* Achievement badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '6px 14px', borderRadius: '50px',
            background: s.gradient, color: 'white',
            fontSize: '0.72rem', fontWeight: '700',
            boxShadow: `0 4px 14px ${s.color}35`,
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
            {s.highlight}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const SuccessStories = () => (
  <ViewerProvider>
  <div className="min-h-screen font-poppins" style={{ overflowX: 'hidden' }}>

    {/* ── HERO ───────────────────────────────────────────────────────────── */}
    <section style={{
      background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 45%, #1a1030 100%)',
      padding: '110px 1.5rem 0',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Animated orbs */}
      {[
        { s: 500, t: '-180px', l: '-150px', c: 'rgba(78,58,105,0.4)', dur: 22 },
        { s: 350, t: '20%', r: '-80px', c: 'rgba(240,180,41,0.12)', dur: 28 },
        { s: 250, b: '-80px', l: '35%', c: 'rgba(255,101,53,0.1)', dur: 18 },
      ].map((o, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', width: o.s, height: o.s, borderRadius: '50%', background: `radial-gradient(circle,${o.c} 0%,transparent 70%)`, top: (o as any).t, left: (o as any).l, right: (o as any).r, bottom: (o as any).b, pointerEvents: 'none' }}
          animate={{ scale: [1, 1.2, 1], rotate: [0, 45, 0] }}
          transition={{ duration: o.dur, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Grid overlay */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.35)', borderRadius: '50px', padding: '7px 22px', marginBottom: '28px', color: '#F0B429', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Star size={14} weight="fill" color="#F0B429" />
            </motion.span>
            Real Students · Real Placements
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '20px', letterSpacing: '-0.02em' }}
        >
          Students Who{' '}
          <span style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Transformed
          </span>
          <br />Their Careers
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '44px', maxWidth: '680px', margin: '0 auto 44px' }}
        >
          Every story here started with <strong style={{ color: 'rgba(255,255,255,0.9)' }}>dedicated mentorship</strong> and the confidence of paying only <strong style={{ color: 'rgba(255,255,255,0.9)' }}>50% upfront</strong>. The rest? Paid after the job offer arrived.
        </motion.p>

        {/* Key stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap', marginBottom: '0' }}
        >
          {[
            { val: '60', unit: 'Day', label: 'Programs' },
            { val: '50%', unit: '', label: 'Pay After Job' },
            { val: '100%', unit: '', label: 'Mentorship' },
            { val: '16', unit: '+', label: 'Stories Below' },
          ].map(({ val, unit, label }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#F0B429', lineHeight: 1 }}>
                {val}<span style={{ fontSize: '1.2rem' }}>{unit}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginTop: '5px', fontWeight: '500' }}>{label}</div>
            </div>
          ))}
        </motion.div>

        {/* Floating student photo strip */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
          style={{ display: 'flex', justifyContent: 'center', gap: '-8px', marginTop: '56px', paddingBottom: '0' }}
        >
          <div style={{ display: 'flex', position: 'relative' }}>
            {[rajeshImg, karthikImg, arjunImg, naveenImg, ajayImg, harishImg, vikramImg, akashImg].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.06 }}
                whileHover={{ y: -8, zIndex: 10 }}
                style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  overflow: 'hidden', border: '3px solid #2b2240',
                  marginLeft: i === 0 ? 0 : '-14px',
                  zIndex: 8 - i, position: 'relative',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
              >
                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </motion.div>
            ))}
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', border: '3px solid #2b2240', marginLeft: '-14px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '800', color: 'white', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 0 }}>
              +8
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom wave */}
      <div style={{ height: '60px', background: 'linear-gradient(to bottom, transparent, #f8f7ff)', marginTop: '40px' }} />
    </section>

    {/* ── MARQUEE TICKER ─────────────────────────────────────────────────── */}
    <div style={{ background: '#2b2240', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: '48px', whiteSpace: 'nowrap', animation: 'marquee 45s linear infinite', width: 'max-content', padding: '14px 0' }}>
        {[...students, ...students].map((s, i) => (
          <span key={i} style={{ fontSize: '0.78rem', fontWeight: '600', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.03em' }}>
            <span style={{ color: '#F0B429' }}>★ </span>{s.name} — {s.role} — {s.highlight}
          </span>
        ))}
      </div>
    </div>

    {/* ── VIDEO TESTIMONIALS ─────────────────────────────────────────────── */}
    <section style={{ background: '#0d0b14', padding: '80px 1.5rem' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '40px' }}
        >
          <p style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.15em', fontWeight: 700, color: '#F0B429', marginBottom: '12px' }}>
            Hear it from them
          </p>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 900, color: 'white', lineHeight: 1.15 }}>
            Watch two students tell their own story
          </h2>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="grid sm:grid-cols-2" style={{ display: 'grid', gap: '20px' }}
        >
          {[
            { src: '/video/success-story-1.mp4', title: 'From training to offer letter', source: 'Success Story 1 — Success Stories Page' },
            { src: '/video/success-story-2.mp4', title: 'A placed student shares their journey', source: 'Success Story 2 — Success Stories Page' },
          ].map(v => (
            <div key={v.src} style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}>
              <GatedVideo src={v.src} sourceLabel={v.source} playLabel="Watch the Success Story" />
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2b2240', background: 'white', padding: '12px 16px', margin: 0 }}>{v.title}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
    <section style={{ background: 'white', padding: '60px 1.5rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="grid-4col-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0' }}>
          {[
            { n: '01', label: 'Pay 50% to Enroll', desc: 'Start with half. No bank loans.', color: '#F0B429', icon: <CurrencyInr size={32} weight="duotone" /> },
            { n: '02', label: 'Expert Mentorship', desc: 'Your dedicated mentor, every day.', color: '#2b2240', icon: <UserCircle size={32} weight="duotone" /> },
            { n: '03', label: 'Real Projects', desc: 'Build industry-grade portfolio.', color: '#10b981', icon: <Rocket size={32} weight="duotone" /> },
            { n: '04', label: 'Pay After Job', desc: 'Remaining 50% after offer letter.', color: '#FF6535', icon: <Medal size={32} weight="duotone" /> },
          ].map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{
                textAlign: 'center', padding: '32px 20px',
                borderRight: i < 3 ? '1px solid rgba(43,34,64,0.07)' : 'none',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px', color: s.color }}>{s.icon}</div>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800', color: 'white', margin: '0 auto 12px' }}>{s.n}</div>
              <div style={{ fontWeight: '800', color: '#2b2240', fontSize: '0.95rem', marginBottom: '6px' }}>{s.label}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── STORIES GRID ───────────────────────────────────────────────────── */}
    <section style={{ padding: '80px 1.5rem 100px', background: '#f8f7ff' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(43,34,64,0.08)', borderRadius: '50px', padding: '6px 20px', fontSize: '0.8rem', fontWeight: '700', color: '#2b2240', marginBottom: '16px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {students.length} Success Stories
          </div>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: '900', color: '#2b2240', marginBottom: '14px', letterSpacing: '-0.02em' }}>
            Real People.{' '}
            <span style={{ background: 'linear-gradient(135deg,#2b2240,#FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Real Careers.
            </span>
          </h2>
          <p style={{ color: '#6b7280', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
            Every card below is a real Vista Tech student — dedicated mentorship, 50% pay after placement, and a job waiting at the end.
          </p>
        </motion.div>

        <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '28px' }}>
          {students.map((s, i) => (
            <StoryCard key={s.name} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>

    {/* ── GOOGLE REVIEWS ──────────────────────────────────────────────────── */}
    <GoogleReviewsSection background="#f8f7ff" />

    {/* ── CTA ────────────────────────────────────────────────────────────── */}
    <section style={{
      padding: '100px 1.5rem',
      background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 50%, #1a1030 100%)',
      textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
      <motion.div
        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{ maxWidth: '680px', margin: '0 auto', position: 'relative', zIndex: 1 }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'inline-flex', padding: '18px', borderRadius: '50%', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.25)' }}>
            <Trophy size={48} weight="duotone" color="#F0B429" />
          </div>
        </div>
        <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: '900', color: 'white', marginBottom: '16px', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          Your Success Story{' '}
          <span style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Starts Here
          </span>
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '44px' }}>
          Join students who bet on themselves — and won. Your dedicated mentor is ready. Pay 50% now. Pay the rest after you get placed.
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}>
            <Link to="/enroll" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '16px 36px', borderRadius: '14px',
              background: 'linear-gradient(135deg,#F0B429,#FF6535)',
              color: 'white', fontWeight: '700', fontSize: '1.05rem',
              textDecoration: 'none', boxShadow: '0 10px 30px rgba(240,180,41,0.4)',
            }}>
              Enroll Now — Pay 50% Later →
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}>
            <Link to="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '16px 32px', borderRadius: '14px',
              border: '1.5px solid rgba(255,255,255,0.2)',
              color: 'white', fontWeight: '600', fontSize: '1.05rem',
              textDecoration: 'none', background: 'rgba(255,255,255,0.07)',
            }}>
              Talk to a Counselor
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>

    {/* Marquee CSS */}
    <style>{`
      @keyframes marquee {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }
    `}</style>
  </div>
  </ViewerProvider>
);

export default SuccessStories;
